import { useCallback, useMemo, useState } from 'react'

import { isCredibleDate } from '@scoir/date'
import { format, isAfter } from 'date-fns'
import schoolClassYearMonthService from 'helpers/school-class-year-month-service'
import { includes } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'

import window from 'common/window'
import { useIsStudentInterestShareEnabled } from 'store/feature-toggle/hooks'
import { selectGroups } from 'store/prospects'

import {
  selectAbandonedScids,
  selectFerpaWaiverEditedByName,
  selectFerpaWaiverEditedDate,
  selectFerpaWaiverSignedDate,
  selectFerpaWaiverStatus,
  selectHasCounselorId,
  selectHighSchool,
  selectHighSchoolDocSupport,
  selectHighSchoolId,
  selectHighSchoolStudentId,
  selectId,
  selectIsAlumni,
  selectPermissions,
  selectSelfReportedHighSchoolDocSupport,
  selectUserRecord,
} from '.'
import { actions } from '.'
import { permissions } from './permissions'

export const useCanUserRequestSpecificRecommendation = () => {
  const isLinkedWithDocSupportForGradYear = useHasHighSchoolDocSupport()
  const highSchool = useSelector(selectHighSchool)
  if (!isLinkedWithDocSupportForGradYear) return true
  return highSchool?.StudentsCanRequestCollegeRecommendations === false ? false : true
}

export const useCanUserRequestFeeWaiver = () => {
  const user = useSelector(selectUserRecord)
  const isSenior = user?.Profile?.GraduationYear === user?.HighSchool?.CurrentSeniorYear
  const hasCounselor = !!user?.CounselorId
  const hasDocSupport = useHasHighSchoolDocSupport()
  return hasCounselor && isSenior && hasDocSupport
}
export const useCanUserSignFERPAWaiver = () => {
  const user = useSelector(selectUserRecord)
  const hasDocSupport = useHasHighSchoolDocSupport()
  return hasDocSupport ? user.HighSchool.FERPA.StudentWaiver : true
}
export const useIsTestUser = () => includes(useSelector(selectPermissions), 'TEST_APPLICATION_STUDENT')
export const useHasLinkedCounselorId = () => useSelector(selectHasCounselorId)
export const useAbandonScid = (scid) => {
  const dispatch = useDispatch()
  return useCallback(() => dispatch(actions.registeredAbandonedScid(scid)), [scid, dispatch])
}
export const useAbandonedScids = () => {
  const scidMap = useSelector(selectAbandonedScids)
  return scidMap
}
export const useCanPerformPreferenceSearch = () => {
  const myPermissions = useSelector(selectPermissions)
  return myPermissions.includes(permissions.PERFORM_PREFERENCE_SEARCH)
}
export const useHasSelfReportedDocSupport = () => useSelector(selectSelfReportedHighSchoolDocSupport)
export const useHasHighSchoolDocSupport = () => useSelector(selectHighSchoolDocSupport)
export const useIsAlumni = () => useSelector(selectIsAlumni)
export const useCanSupportNonCounselorRecommendations = () => {
  const isSelfReportedDocSupported = useHasSelfReportedDocSupport()
  const isLinkedHighSchoolDocSupported = useHasHighSchoolDocSupport()
  const supportsDocuments = isSelfReportedDocSupported || isLinkedHighSchoolDocSupported
  const isAlumni = useIsAlumni()
  if (isAlumni) {
    return true
  }
  return !supportsDocuments
}
export const useUser = () => useSelector(selectUserRecord)
export const useUserId = () => useSelector(selectId)
export const useHighSchoolId = () => useSelector(selectHighSchoolId)
export const useHighSchoolStudentId = () => useSelector(selectHighSchoolStudentId)
export const useFERPAWaiverStatus = () => useSelector(selectFerpaWaiverStatus)
export const useFERPAWaiverSignedDate = () => useSelector(selectFerpaWaiverSignedDate)
export const useFERPAWaiverEditedDate = () => useSelector(selectFerpaWaiverEditedDate)
export const useFERPAWaiverEditedByName = () => useSelector(selectFerpaWaiverEditedByName)
export const useFormattedFERPAWaiverSignedDate = () => {
  const date = useFERPAWaiverSignedDate()
  const editDate = useFERPAWaiverEditedDate()
  const editByName = useFERPAWaiverEditedByName()
  return useMemo(() => {
    const isSignedDateCredible = isCredibleDate(date)
    const isEditDateCredible = isCredibleDate(editDate)
    const isEditedAfterSigning =
      isEditDateCredible && (!isSignedDateCredible || isAfter(new Date(editDate), new Date(date)))
    const relevantDate = isEditedAfterSigning ? editDate : isSignedDateCredible ? date : ''
    let dateString = ''
    if (relevantDate) {
      const initialDate = new Date(relevantDate)
      const d = initialDate.getUTCDate()
      const m = initialDate.getUTCMonth()
      const y = initialDate.getUTCFullYear()
      const localDateFromUTCDate = new Date(y, m, d)
      dateString = format(localDateFromUTCDate, 'MM/dd/yyyy')
    }
    return [dateString, editByName]
  }, [editByName, date, editDate])
}

export const useIsEarlyCareerReadinessStudent = () => {
  const [isECR, setIsECR] = useState(false)
  window.setEarlyCareerReadiness = (v) => setIsECR(() => v)
  // TODO: remove state and implement real solution using permissions once ASOPS is ready
  // MUST BE FALSE until we have actual way to tell if ECR is enabled for a student

  return isECR
}
export const useECREducationCollegePrograms = () => {
  const myPermissions = useSelector(selectPermissions)
  const canViewMiddleSchoolCollegeEvents = myPermissions.includes(permissions.VIEW_MS_GRADE_COLLEGE_EVENTS)
  const canViewHighSchoolCollegeEvents = myPermissions.includes(permissions.VIEW_HS_GRADE_COLLEGE_EVENTS)
  return canViewMiddleSchoolCollegeEvents && !canViewHighSchoolCollegeEvents
}
export const useCanDownloadProfile = () => {
  const myPermissions = useSelector(selectPermissions)
  return myPermissions.includes(permissions.CAN_DOWNLOAD_STUDENT_PROFILE)
}

export const useAcceptanceLikelihoodLabels = () => {
  const hs = useSelector(selectHighSchool)
  return {
    FarReach: hs?.AcceptanceLikelihoodFarReachLabel || 'Far Reach',
    Reach: hs?.AcceptanceLikelihoodReachLabel || 'Reach',
    Level: hs?.AcceptanceLikelihoodLevelLabel || 'Level',
    Likely: hs?.AcceptanceLikelihoodLikelyLabel || 'Likely',
    VeryLikely: hs?.AcceptanceLikelihoodVeryLikelyLabel || 'Very Likely',
  }
}

export const useAcceptanceLikelihoodPercentageThresholds = () => {
  const hs = useSelector(selectHighSchool)
  return (
    hs?.AcceptanceLikelihoodPercentageThresholds || {
      FarReach: 0,
      Reach: 20,
      Level: 40,
      Likely: 60,
      VeryLikely: 80,
    }
  )
}

export const useUpdateProfile = () => {
  const dispatch = useDispatch()
  return useCallback((profile) => dispatch(actions.updateProfile(profile)), [dispatch])
}

export const useRefetchUser = () => {
  const dispatch = useDispatch()
  return useCallback(() => dispatch(actions.fetchUser()), [dispatch])
}

export const useCanRequestRecommendations = () => {
  const user = useSelector(selectUserRecord)
  const hs = useSelector(selectHighSchool)
  const hasDocumentSupport = useHasHighSchoolDocSupport()
  return hasDocumentSupport
    ? !!hs &&
        hs?.SupportsDocuments &&
        schoolClassYearMonthService.isAfter(
          user?.Profile?.GraduationYear,
          hs?.CurrentSeniorYear,
          hs?.RecommendationStartMonth,
          hs?.RecommendationStartYear,
        ) &&
        hs?.StudentsCanRequestRecommendations
    : true
}

export const useShareInterest = (scid) => {
  const studentInterestShareEnabled = useIsStudentInterestShareEnabled()
  const user = useSelector(selectUserRecord)
  const preferenceNotSet = !user?.StudentInterestSharingPreference
  const preferenceIsAskMeEveryTime = user?.StudentInterestSharingPreference?.preferenceType === 'ask_me_every_time'

  const prospects = useSelector(selectGroups)
  const alreadySharedInterestWithCollege = scid
    ? prospects?.notInterested?.find((p) => p.CollegeDetail.Scid === scid && p.StudentSharedInterest)
    : false

  return (
    studentInterestShareEnabled && (preferenceNotSet || preferenceIsAskMeEveryTime) && !alreadySharedInterestWithCollege
  )
}
